/**
 * Profile 页面业务逻辑 Hook
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, App } from "antd";
import { changePassword, getUserInfoDetail, ssoLogout } from "@ifa/utils";
import type { UserInfoDetail } from "@ifa/utils";
import { useIOSConfirm } from "../../../hooks/useIOSConfirm";

// 模拟保司工号数据（后续可以从 API 获取）
const mockInsuranceCompanies = [
  { id: "1", name: "Sunlife 永明金融", code: "Y066ZG", logo: "" },
  { id: "2", name: "Prudential 保誠", code: "03222544", logo: "" },
  { id: "3", name: "ChinaLife 中國人壽", code: "B30001100000327", logo: "" },
  { id: "4", name: "CTFLife 周大福人壽", code: "IFA-0438928-00304", logo: "" },
  { id: "5", name: "FWD 富衛", code: "Y066ZG", logo: "" },
  { id: "6", name: "CHUBB 安達", code: "Y066ZG", logo: "" },
  { id: "7", name: "YFLife 萬通", code: "Y066ZG", logo: "" },
  { id: "8", name: "Manulife 宏利", code: "Y066ZG", logo: "" },
];

export function useProfile() {
  const navigate = useNavigate();
  const { message: messageApi } = App.useApp();
  const { confirm } = useIOSConfirm();
  const [userInfo, setUserInfo] = useState<UserInfoDetail | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [insuranceCompanies] = useState(mockInsuranceCompanies);
  const [fetchLoading, setFetchLoading] = useState(false);

  // 获取用户详细信息
  const fetchUserInfo = async () => {
    try {
      const uid = localStorage.getItem("uid");
      if (!uid) {
        console.error("No uid found in localStorage");
        return;
      }

      setFetchLoading(true);
      const data = await getUserInfoDetail(Number(uid));
      setUserInfo(data);
    } catch (error) {
      console.error("Failed to fetch user info:", error);
    } finally {
      setFetchLoading(false);
    }
  };

  // 初始化用户信息
  useEffect(() => {
    fetchUserInfo();
  }, []);

  // 打开修改密码弹窗
  const handleOpenChangePassword = () => {
    setIsModalOpen(true);
    form.resetFields();
  };

  // 关闭修改密码弹窗
  const handleCloseChangePassword = () => {
    setIsModalOpen(false);
  };

  // 修改密码
  const handleChangePassword = async (values: any) => {
    console.log(values);
    if (values.newPassword !== values.confirmPassword) {
      return;
    }

    setLoading(true);
    try {
      await changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      messageApi.success("密碼修改成功，請重新登入");
      setIsModalOpen(false);
      form.resetFields();
      setTimeout(() => {
        // 使用 SSO 登出，会清除认证信息并跳转到统一登录页
        ssoLogout();
      }, 1000);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // 退出登录
  const handleLogout = () => {
    confirm({
      title: "確認退出",
      content: "您確定要登出嗎？",
      okText: "確認",
      cancelText: "取消",
      onOk: () => {
        messageApi.success("已登出");
        // 使用 SSO 登出，会清除认证信息并跳转到统一登录页
        ssoLogout();
      },
    });
  };

  return {
    userInfo,
    isModalOpen,
    loading,
    form,
    insuranceCompanies,
    fetchLoading,
    handleOpenChangePassword,
    handleCloseChangePassword,
    handleChangePassword,
    handleLogout,
  };
}
